/**
 * Pronto · CreatingAnnuncioBanner — iter v33 r3.
 *
 * User M1465 + M1471: «cosa succede se io cambio sezione? Lascia sempre un
 * messaggio che l'annuncio è in creazione» + «mettimi un contatore del tempo,
 * cioè uno capisce che è tutto ok». L'utente NON deve mai entrare nell'annuncio
 * finché non è pronto (sennò si impalla).
 *
 * Tre stati:
 *   1. "Preparo l'annuncio · 01:23 · 3/8 foto pronte" (in lavorazione)
 *      - sfondo nero, sparkle pulsante, NON cliccabile (porta nessuno dentro
 *        l'annuncio finché non è pronto)
 *   2. "Annuncio pronto · Apri →" (tutti i job done)
 *      - sfondo verde, check, cliccabile → naviga al PropertyDetail
 *   3. "Annuncio creato con problemi · Apri" (qualche job in errore)
 *      - sfondo ambra, warning
 *
 * Polling ogni 5s su `/properties/{pid}/kit-jobs`. Hard timeout 15 min.
 *
 * FIX critico iter v33 r3: prima il banner controllava `["done", "failed"]`
 * ma il backend usa `["ready", "error", "skipped"]` → il banner non capiva
 * mai quando finiva il lavoro e restava bloccato 3 min fino all'hard timeout.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Sparkle, X as XIcon, Check, Warning } from "@phosphor-icons/react";
import { api } from "../lib/api";

const STORAGE_KEY = "pronto_in_creazione_v1";
const POLL_INTERVAL_MS = 5000;
// iter v33 r3 — hard timeout esteso da 3 min a 15 min: enhance_photos su 8 foto
// in parallelo (3 batch da 3) può richiedere ~5 minuti reali. 3 min era troppo
// aggressivo, il banner si auto-toglieva mentre il kit lavorava ancora.
const HARD_TIMEOUT_MS = 15 * 60 * 1000;

// iter v33 r62 · Il banner ora polla `/api/properties/{pid}` per determinare
// quando l'annuncio e' "pronto" (ai_description LLM finita, foto attaccate).
// Il polling su kit-jobs e' stato deprecato con r26 (zero automatismo foto).
// Gli set ACTIVE/FINAL_STATES restano definiti per compat con eventuali
// consumer futuri ma non sono piu' usati nella logica di ready.
// eslint-disable-next-line no-unused-vars
const _JOB_STATES = { active: ["queued", "processing"], final: ["ready", "error", "skipped"] };

export function markAnnuncioInCreazione(payload) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        pid: payload.pid,
        title: payload.title || "Nuovo annuncio",
        addr: payload.addr || "",
        startedAt: Date.now(),
      }),
    );
    window.dispatchEvent(new Event("pronto:in-creazione"));
  } catch {
    /* private mode, ignored */
  }
}

export function clearAnnuncioInCreazione() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new Event("pronto:in-creazione"));
  } catch {
    /* ignored */
  }
}

// Format mm:ss helper
function fmtElapsed(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function CreatingAnnuncioBanner() {
  const navigate = useNavigate();
  const location = useLocation();
  const [info, setInfo] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [jobsDone, setJobsDone] = useState(0);
  const [jobsTotal, setJobsTotal] = useState(0);
  const [enhancedDone, setEnhancedDone] = useState(0);
  const [enhancedTotal, setEnhancedTotal] = useState(0);
  const [hasErrors, setHasErrors] = useState(false);
  const [allDone, setAllDone] = useState(false);
  // iter v33 r3 — Elapsed counter (mm:ss) aggiornato ogni 1s. Reset al pid.
  const [elapsedMs, setElapsedMs] = useState(0);
  const completedAtRef = useRef(null);

  // Sync con storage event + custom event
  useEffect(() => {
    const onSync = () => {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const next = raw ? JSON.parse(raw) : null;
        setInfo(next);
        if (!next) {
          // Reset state quando il banner viene tolto
          setJobsDone(0);
          setJobsTotal(0);
          setEnhancedDone(0);
          setEnhancedTotal(0);
          setHasErrors(false);
          setAllDone(false);
          setElapsedMs(0);
          completedAtRef.current = null;
        }
      } catch {
        /* ignored */
      }
    };
    window.addEventListener("storage", onSync);
    window.addEventListener("pronto:in-creazione", onSync);
    return () => {
      window.removeEventListener("storage", onSync);
      window.removeEventListener("pronto:in-creazione", onSync);
    };
  }, []);

  // Tick timer mm:ss ogni 1s finché non è completato
  useEffect(() => {
    if (!info?.startedAt || allDone) return;
    const update = () => setElapsedMs(Date.now() - info.startedAt);
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [info, allDone]);

  const checkJobs = useCallback(async () => {
    if (!info?.pid) return;
    // Hard timeout 15 min: dopo questo limite togliamo il banner anche se
    // qualcosa è rimasto pending (l'utente vedrà comunque lo stato sul
    // PropertyDetail).
    if (Date.now() - (info.startedAt || 0) > HARD_TIMEOUT_MS) {
      clearAnnuncioInCreazione();
      setInfo(null);
      return;
    }
    // iter v33 r62 · User: «non farmi entrare dentro finche' non e' proprio
    // tutto carico». Sopralluogo → to-property NON crea piu' kit_jobs
    // (r26 tolto l'automatismo foto). Quello che gira in background e':
    //   1. generate_description_in_background (LLM, ~30-45s per 3 paragrafi)
    //   2. foto gia' collegate sync durante to-property
    // Criterio "ready":
    //   - ai_description > 250 char (il fallback ex.notes e' ~120-180 char
    //     mentre la LLM completa arriva a 800-1500 char)
    //   - OR >= 2 paragrafi (LLM ha struttura 3 paragrafi separati da \n\n)
    //   - foto attese tutte attaccate (o zero attese)
    // Fallback: dopo 60s consideriamo comunque ready per non tenere ostaggio
    // l'utente (se LLM ha fallito, resta il fallback notes, comunque leggibile).
    try {
      const { data } = await Promise.race([
        api.get(`/properties/${info.pid}`),
        new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 6000)),
      ]);
      const p = data || {};
      const photoCount = Array.isArray(p.photos) ? p.photos.length : 0;
      const expected = p.expected_photo_count || 0;
      const aiDesc = (p.ai_description || "").trim();
      const paragraphs = aiDesc ? aiDesc.split(/\n\n+/).filter((s) => s.trim().length > 0).length : 0;
      const descReady = aiDesc.length > 250 || paragraphs >= 2;
      const photosReady = expected === 0 || photoCount >= expected;
      const elapsedSec = (Date.now() - (info.startedAt || 0)) / 1000;
      const timeoutReady = elapsedSec > 60;

      setEnhancedDone(photoCount);
      setEnhancedTotal(expected);
      const stepDone = (descReady ? 1 : 0) + (photosReady ? 1 : 0);
      setJobsDone(stepDone);
      setJobsTotal(2);
      setHasErrors(false);

      const ready = (descReady && photosReady) || timeoutReady;
      if (ready) {
        if (!completedAtRef.current) completedAtRef.current = Date.now();
        setAllDone(true);
        // Auto-clear dopo 60s dal completamento: l'utente ha tempo di
        // toccare la pill verde "Apri". Se ignora, il banner sparisce
        // e l'annuncio resta accessibile dalla lista annunci.
        const sinceCompletion = Date.now() - completedAtRef.current;
        if (sinceCompletion > 60000) {
          clearAnnuncioInCreazione();
          setInfo(null);
        }
      }
    } catch (e) {
      if (e?.response?.status === 404) {
        clearAnnuncioInCreazione();
        setInfo(null);
      }
    }
  }, [info]);

  useEffect(() => {
    if (!info?.pid) return;
    checkJobs();
    const id = setInterval(checkJobs, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [info, checkJobs]);

  if (!info?.pid) return null;
  // iter v33 r62 · User: «lasciami usare l'app in qualsiasi altro modo,
  // andare in home, nelle altre sezioni». Il banner e' ora visibile su
  // TUTTE le pagine dashboard tranne il PropertyDetail dell'annuncio in
  // creazione (dentro c'e' gia' lo skeleton, sarebbe ridondante).
  const currentPath = location.pathname || "";
  // Nascondi il banner SOLO quando l'utente e' gia' dentro l'annuncio in creazione.
  if (currentPath.endsWith(`/property/${info.pid}`)) return null;
  // Nascondi anche su pagine pubbliche (share, stima, agente) o auth (utente non loggato).
  const HIDDEN_PATH_PREFIXES = ["/auth", "/agente/", "/stima/", "/share/", "/annuncio/", "/quanto-vale", "/anteprima-"];
  if (HIDDEN_PATH_PREFIXES.some((p) => currentPath.startsWith(p))) return null;

  // ── Render ────────────────────────────────────────────────────────────
  const elapsed = fmtElapsed(elapsedMs);
  const subtitle = enhancedTotal > 0
    ? `${enhancedDone}/${enhancedTotal} foto pronte · ${elapsed}`
    : (jobsTotal > 0
        ? `${jobsDone}/${jobsTotal} step pronti · ${elapsed}`
        : `preparazione in corso · ${elapsed}`);

  const isReady = allDone && !hasErrors;
  const isReadyWithErrors = allDone && hasErrors;

  // Click handler: solo se pronto naviga al PropertyDetail. Se in corso,
  // mostra un toast (l'utente NON deve entrare nell'annuncio incompleto).
  const handleClick = () => {
    if (isReady || isReadyWithErrors) {
      clearAnnuncioInCreazione();
      navigate(`/dashboard/property/${info.pid}`);
    }
    // Altrimenti: NO-OP. L'utente clicca a vuoto fino a quando il banner
    // diventa verde "Apri".
  };

  // iter v33 r13 · User M1491: «potential» → uniforma con il chip del
  // PropertyDetail (r12). Passa da pill nero a pill CREAM+VERDE con
  // progress bar, in linea con il resto della UI Pronto.
  const workingBg = "bg-[#F1F4ED] border border-[#7C8B6E]/30";
  const readyBg = "bg-[#5E7048] border border-[#4F5F3C]";
  const errorBg = "bg-[#B58A4E] border border-[#A07B42]";
  const bgClass = isReady ? readyBg : isReadyWithErrors ? errorBg : workingBg;
  const textColor = isReady || isReadyWithErrors ? "text-white" : "text-[#1C1C1A]";
  const iconBg = isReady || isReadyWithErrors ? "bg-white/20" : "bg-white border border-[#7C8B6E]/30";
  const iconColor = isReady || isReadyWithErrors ? "text-white" : "text-[#7C8B6E]";

  // Progress % (solo se sappiamo total > 0)
  const totalItems = enhancedTotal > 0 ? enhancedTotal : jobsTotal;
  const doneItems = enhancedTotal > 0 ? enhancedDone : jobsDone;
  const pct = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 8;

  // iter v33 r14 · User M1492: «metti quanto manca». ETA basata sul tempo
  // già trascorso: se abbiamo fatto N foto in T secondi, per le mancanti
  // servono (T/N) * (Total-Done). Se ancora zero foto pronte, uso stima
  // statica (~150s/foto per Nano Banana Pro).
  const remaining = Math.max(0, totalItems - doneItems);
  let etaLabel = null;
  if (!isReady && !isReadyWithErrors && totalItems > 0 && remaining > 0) {
    let etaSec;
    if (doneItems > 0 && elapsedMs > 4000) {
      const perItem = (elapsedMs / 1000) / doneItems;
      etaSec = Math.round(perItem * remaining);
    } else {
      etaSec = Math.ceil(remaining / 3) * 80; // iter v33 r18: batch di 3 in parallelo, ~80s a batch (misura reale)
    }
    if (etaSec < 60) {
      etaLabel = `~${etaSec}s rimanenti`;
    } else {
      const m = Math.round(etaSec / 60);
      etaLabel = `~${m} minut${m === 1 ? "o" : "i"} rimanenti`;
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!isReady && !isReadyWithErrors}
      className={`fixed left-1/2 -translate-x-1/2 bottom-[88px] z-[80] flex items-center gap-3 pl-3 pr-4 py-2.5 ${bgClass} ${textColor} rounded-full shadow-lg ${isReady || isReadyWithErrors ? "active:scale-95 cursor-pointer" : "cursor-default"} transition-all max-w-[85vw] min-w-[260px]`}
      style={{
        paddingBottom: "max(0.625rem, env(safe-area-inset-bottom))",
        // iter v33 r6 — User M1483: «la homepage non riesco a scrollarla».
        // Root cause: quando il banner era in stato "working" (button
        // disabled ma ancora un <button>), su iOS WKWebView i gesti di
        // swipe che passavano sopra il banner venivano consumati dal
        // button anziché scrollare il document. Fix: `pointer-events: none`
        // quando in lavorazione → i gesti passano attraverso al body.
        // Solo la X di dismiss resta cliccabile (pointer-events: auto sotto).
        // Quando "ready" invece riprende pointer-events: auto normale.
        pointerEvents: (isReady || isReadyWithErrors) ? "auto" : "none",
        touchAction: "manipulation",
      }}
      data-testid="creating-annuncio-banner"
      data-state={isReady ? "ready" : isReadyWithErrors ? "ready-with-errors" : "working"}
    >
      <div className={`relative w-8 h-8 inline-flex items-center justify-center rounded-full shrink-0 ${iconBg}`}>
        {isReady ? (
          <Check size={14} weight="bold" className={iconColor} />
        ) : isReadyWithErrors ? (
          <Warning size={14} weight="fill" className={iconColor} />
        ) : (
          <Sparkle size={14} weight="fill" className={`${iconColor} animate-pulse`} />
        )}
      </div>
      <div className="flex-1 text-center min-w-0">
        <div className={`text-[13px] font-semibold leading-tight truncate ${textColor}`}>
          {isReady
            ? "Annuncio pronto · Apri"
            : isReadyWithErrors
              ? "Annuncio quasi pronto · Apri"
              : "Sto preparando l'annuncio…"}
        </div>
        {isReady || isReadyWithErrors ? (
          <div className={`text-[11px] leading-tight truncate mt-0.5 opacity-80`}>
            {isReady ? `Completato in ${elapsed}` : `Qualche step ha avuto problemi · ${elapsed}`}
          </div>
        ) : (
          <>
            {/* Sottotitolo con titolo annuncio (accorciato) + timer */}
            <div className="text-[10.5px] leading-tight truncate mt-0.5 text-[#6B6A65]" data-testid="creating-annuncio-banner-subtitle">
              {info.title || "Nuovo annuncio"} · {elapsed}
            </div>
            {/* Barra progresso */}
            <div className="mt-1.5 h-1 rounded-full bg-white/70 overflow-hidden">
              <div
                className="h-full bg-[#7C8B6E] transition-all duration-500 ease-out"
                style={{ width: `${pct}%` }}
                data-testid="creating-annuncio-banner-bar"
              />
            </div>
          </>
        )}
      </div>
      {/* La X di dismiss resta SOLO quando il lavoro è ancora in corso; appena
          è pronto il banner diventa CTA principale e il dismiss sparisce per
          non far perdere all'utente l'azione "Apri". */}
      {!isReady && !isReadyWithErrors && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            clearAnnuncioInCreazione();
            setInfo(null);
          }}
          role="button"
          tabIndex={0}
          aria-label="Nascondi notifica"
          className="ml-1 p-1.5 -mr-1 hover:bg-white/50 rounded-full cursor-pointer inline-flex items-center justify-center text-[#6B6A65]"
          style={{ pointerEvents: "auto" }}
          data-testid="creating-annuncio-banner-dismiss"
        >
          <XIcon size={12} weight="bold" />
        </span>
      )}
    </button>
  );
}
